<script setup>
import { Link, router, useForm, usePage } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import FormInput from '@/components/form/FormInput.vue'
import { useIdCodeValidation } from '@/composables/useIdCodeValidation'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import FormSelect from '../../components/form/FormSelect.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

const props = defineProps({
  permissions: Array,
  users: Array,
  pendingInvitations: Array,
  companies: Array,
  roles: Array,
  showArchived: { type: Boolean, default: false },
})

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()
const isAddingUser = ref(false)
const showArchivedRef = ref(props.showArchived)

// ToggleSwitch doesn't emit @change in PrimeVue v4 (only update:modelValue
// via v-model). Watch the ref directly so the reload fires reliably.
watch(showArchivedRef, value => {
  router.reload({
    data: { show_archived: value ? 1 : 0 },
    only: ['users', 'showArchived'],
    preserveState: true,
    preserveScroll: true,
  })
})

const restoreUser = user => {
  router.post(route('admin.user.restore', user.id), {}, {
    preserveScroll: true,
    preserveState: true,
    onSuccess: () => {
      toast.add({
        severity: 'success',
        summary: t('messages.updated'),
        detail: t('messages.admin.user.restored_success'),
        life: 3000,
      })
    },
  })
}

const rowClass = data => (data.deleted_at ? 'opacity-60' : '')

// Full Skeleton ONLY before the deferred users prop has ever resolved (true
// first paint). For every subsequent in-page action (archive, permission
// toggle, show-archived toggle, invite) the table silently updates in place:
// `lastResolvedUsers` keeps the old rows visible during the brief undefined
// window, no overlay spinner — so interactive cells (MultiSelect, buttons)
// stay clickable without blocking.
const placeholderRows = Array.from({ length: 16 }, (_, i) => ({ _placeholder: true, _key: i }))
const hasResolvedOnce = ref(false)
const lastResolvedUsers = ref(undefined)

watch(
  () => props.users,
  next => {
    if (next !== undefined) {
      lastResolvedUsers.value = next
      hasResolvedOnce.value = true
    }
  },
  { immediate: true },
)

const firstLoad = computed(() => !hasResolvedOnce.value)
const tableValue = computed(() => {
  if (firstLoad.value) return placeholderRows
  return props.users ?? lastResolvedUsers.value ?? []
})

const page = usePage()
const currentUserId = computed(() => page.props.auth?.user?.id ?? null)
const isSelfRow = data => currentUserId.value !== null && data.id === currentUserId.value

// RED-286: the method+url overload makes this a precognitive form, so the
// Isikukood field can be checked against the server rule before submit.
const form = useForm('post', route('admin.invitation.store'), {
  email: null,
  id_code: null,
  company_id: null,
  role: null,
})

useIdCodeValidation(form)

const availableRoles = computed(() => {
  if (!props.roles?.length) return []
  return props.roles.filter(r => r.company_id === null || r.company_id === form.company_id)
})

// Reset the role when the company changes so a stale cross-company selection
// doesn't survive into the POST and trip the backend validator.
watch(() => form.company_id, () => { form.role = null })

const deleteUser = user => {
  confirm.require({
    message: t('messages.admin.user.archive_confirm', {
      name: user.firstname + ' ' + user.lastname,
    }),
    header: t('messages.admin.user.archive_confirm_header'),
    accept: () => {
      router.delete(route('admin.user.destroy', user.id), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          toast.add({
            severity: 'success',
            summary: t('messages.updated'),
            detail: t('messages.admin.user.archive_success', {
              name: user.firstname + ' ' + user.lastname,
            }),
            life: 3000,
          })
        },
        onError: () => {
          toast.add({
            severity: 'error',
            summary: t('messages.error'),
            detail: t('messages.admin.user.archive_error', {
              name: user.firstname + ' ' + user.lastname,
            }),
            life: 3000,
          })
        },
      })
    },
  })
}

const inviteUser = () => {
  isAddingUser.value = true

  form.post(route('admin.invitation.store'), {
    preserveScroll: true,
    preserveState: true,
    only: ['pendingInvitations', 'flash', 'errors'],
    onSuccess: () => {
      form.reset()
      toast.add({
        severity: 'success',
        summary: t('messages.added'),
        detail: t('messages.invitations.admin.tab_title'),
        life: 3000,
      })
    },
    onError: errors => {
      const detail = Object.values(errors)[0] ?? t('messages.admin.user.added_error_detail')
      toast.add({
        severity: 'error',
        summary: t('messages.error'),
        detail,
        life: 4000,
      })
    },
    onFinish: () => {
      isAddingUser.value = false
    },
  })
}

const resendInvitation = invitation => {
  router.post(route('admin.invitation.resend', invitation.id), {}, {
    preserveScroll: true,
    preserveState: true,
    only: ['pendingInvitations', 'flash', 'errors'],
    onSuccess: () => {
      toast.add({
        severity: 'success',
        summary: t('messages.updated'),
        detail: t('messages.invitations.admin.resent_success', { email: invitation.email }),
        life: 3000,
      })
    },
    onError: errors => {
      const detail = Object.values(errors)[0] ?? t('messages.error')
      toast.add({ severity: 'error', summary: t('messages.error'), detail, life: 4000 })
    },
  })
}

const revokeInvitation = invitation => {
  confirm.require({
    message: t('messages.invitations.admin.revoke_confirm', { email: invitation.email }),
    header: t('messages.invitations.admin.revoke_confirm_header'),
    accept: () => {
      router.delete(route('admin.invitation.destroy', invitation.id), {
        preserveScroll: true,
        preserveState: true,
        only: ['pendingInvitations', 'flash'],
        onSuccess: () => {
          toast.add({
            severity: 'success',
            summary: t('messages.updated'),
            detail: t('messages.invitations.admin.revoked_success'),
            life: 3000,
          })
        },
      })
    },
  })
}

// RED-286: correct (or add) the isikukood on a pending invitation. Errors
// stay inline in the dialog; the typed value is kept until it saves.
const idCodeDialog = ref({ visible: false, invitation: null })
const idCodeForm = useForm(
  'patch',
  () => route('admin.invitation.id-code', idCodeDialog.value.invitation?.id ?? 0),
  { id_code: '' },
)

useIdCodeValidation(idCodeForm)

const openIdCodeDialog = invitation => {
  idCodeForm.reset()
  idCodeForm.clearErrors()
  idCodeDialog.value = { visible: true, invitation }
}

const saveIdCode = () => {
  const invitation = idCodeDialog.value.invitation
  idCodeForm.patch(route('admin.invitation.id-code', invitation.id), {
    preserveScroll: true,
    preserveState: true,
    only: ['pendingInvitations', 'flash', 'errors'],
    onSuccess: () => {
      idCodeDialog.value.visible = false
      idCodeForm.reset()
      toast.add({
        severity: 'success',
        summary: t('messages.updated'),
        detail: t('messages.invitations.admin.id_code_updated_success', { email: invitation.email }),
        life: 3000,
      })
    },
  })
}

const permissionChange = (userId, permissionsOptions) => {
  router.put(
    route('admin.user.update', userId),
    {
      permissions: permissionsOptions.map(permission => permission.value),
    },
    {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        toast.add({
          severity: 'success',
          summary: t('messages.updated'),
          detail: t('messages.admin.user.permission_updated_success_detail'),
          life: 3000,
        })
      },
      onError: () => {
        toast.add({
          severity: 'error',
          summary: t('messages.error'),
          detail: t('messages.admin.user.permission_updated_error_detail'),
          life: 3000,
        })
      },
    },
  )
}

/**
 * Get permissions available for a user to be assigned as additional permissions.
 * Excludes permissions the user already has through their role(s).
 */
const getAvailablePermissions = user => {
  const rolePermissionValues = (user.role_permissions || []).map(p => p.value)
  return props.permissions.filter(p => !rolePermissionValues.includes(p.value))
}

/**
 * Get user's direct permissions that are NOT covered by their role.
 * Filters out stale/redundant permissions that now come from role.
 */
const getRelevantPermissions = user => {
  const rolePermissionValues = (user.role_permissions || []).map(p => p.value)
  return (user.permissions || []).filter(
    p => !rolePermissionValues.includes(p.value),
  )
}

const toggleAllPermissions = userData => {
  const available = getAvailablePermissions(userData)
  const relevant = getRelevantPermissions(userData)
  if (relevant.length === available.length) {
    userData.permissions = []
  } else {
    userData.permissions = JSON.parse(JSON.stringify(available))
  }
  permissionChange(userData.id, userData.permissions)
}
</script>

<template>
  <Card class="mt-4">
    <template #title>{{ $t('messages.admin.user.invite_new') }}</template>
    <template #content>
      <Fluid>
        <form class="grid grid-cols-1 md:grid-cols-12 gap-3 items-start" @submit.prevent="inviteUser()">
          <div class="md:col-span-5">
            <label class="text-sm text-gray-600 block mb-1">
              {{ $t('messages.admin.user.email') }}
            </label>
            <FormInput
              v-model="form.email"
              :form="form"
              field="email"
              :placeholder="$t('messages.admin.user.email_placeholder')"
            />
          </div>
          <div class="md:col-span-5">
            <label class="text-sm text-gray-600 block mb-1">
              {{ $t('messages.admin.user.id_code') }}
            </label>
            <FormInput
              v-model="form.id_code"
              :form="form"
              field="id_code"
              :placeholder="$t('messages.admin.user.id_code_placeholder')"
              live-validate
              data-testid="invite-id-code"
            >
              <template #hint="{ valid }">
                <span :class="valid ? 'text-green-700' : 'italic'">
                  <i v-if="valid" class="pi pi-check-circle text-xs mr-1" />
                  {{
                    valid
                      ? $t('messages.admin.user.id_code_valid')
                      : $t('messages.admin.user.id_code_invite_hint')
                  }}
                </span>
              </template>
            </FormInput>
          </div>
          <!-- Keeps e-post/isikukood the same width as ettevõte/roll below. -->
          <div class="hidden md:block md:col-span-2" />
          <div class="md:col-span-5">
            <label class="text-sm text-gray-600 block mb-1">
              {{ $t('messages.admin.user.company') }}
            </label>
            <FormSelect
              v-model="form.company_id"
              :form="form"
              field="company_id"
              :placeholder="$t('messages.admin.select.company')"
              optionValue="id"
              optionLabel="name"
              :options="companies"
            />
          </div>
          <div class="md:col-span-5">
            <label class="text-sm text-gray-600 block mb-1">
              {{ $t('messages.admin.user.role') }}
            </label>
            <FormSelect
              v-model="form.role"
              :form="form"
              field="role"
              :placeholder="$t('messages.admin.select.role')"
              :options="availableRoles"
              optionValue="value"
              optionLabel="label"
              :disabled="!form.company_id || !roles?.length"
            />
            <p class="text-xs text-gray-400 italic mt-1 leading-4" :class="{ invisible: !!form.company_id }">
              {{ $t('messages.admin.user.select_company_first') }}
            </p>
          </div>
          <div class="md:col-span-2 md:mt-6">
            <Button type="submit" class="w-full" :disabled="isAddingUser || !form.role || !form.id_code || form.invalid('id_code')">
              {{ isAddingUser ? `${$t('messages.sending')}...` : $t('messages.send') }}
            </Button>
          </div>
        </form>
      </Fluid>
    </template>
  </Card>

  <!-- Pending invitations — only shown when there's at least one outstanding. -->
  <Card v-if="pendingInvitations && pendingInvitations.length > 0" class="mt-6">
    <template #title>
      <div class="flex items-center gap-2">
        <span>{{ $t('messages.invitations.admin.tab_title') }}</span>
        <Tag :value="pendingInvitations.length" severity="info" class="text-xs" />
      </div>
    </template>
    <template #content>
      <DataTable :value="pendingInvitations" stripedRows>
        <Column :header="$t('messages.invitations.admin.columns.email')">
          <template #body="{ data }">
            <div class="flex flex-col">
              <span class="font-medium">{{ data.email }}</span>
              <span class="text-xs text-gray-500">{{ data.company?.name }}</span>
            </div>
          </template>
        </Column>
        <Column :header="$t('messages.invitations.admin.columns.role')">
          <template #body="{ data }">{{ data.role_label }}</template>
        </Column>
        <Column :header="$t('messages.invitations.admin.columns.id_code')">
          <template #body="{ data }">
            <Tag
              :value="
                data.has_id_code
                  ? $t('messages.invitations.admin.id_code_set')
                  : $t('messages.invitations.admin.id_code_missing')
              "
              :severity="data.has_id_code ? 'success' : 'warn'"
            />
          </template>
        </Column>
        <Column :header="$t('messages.invitations.admin.columns.inviter')">
          <template #body="{ data }">{{ data.inviter?.full_name || '-' }}</template>
        </Column>
        <Column :header="$t('messages.invitations.admin.columns.sent_at')">
          <template #body="{ data }">{{ data.created_at }}</template>
        </Column>
        <Column :header="$t('messages.invitations.admin.columns.expires_at')">
          <template #body="{ data }">{{ data.expires_at }}</template>
        </Column>
        <Column :header="$t('messages.invitations.admin.columns.resent_count')">
          <template #body="{ data }">{{ data.resent_count }}</template>
        </Column>
        <Column :header="$t('messages.invitations.admin.columns.actions')">
          <template #body="{ data }">
            <div class="flex gap-2">
              <Button
                type="button"
                outlined
                size="small"
                :label="$t('messages.invitations.admin.action.edit_id_code')"
                data-testid="invitation-edit-id-code"
                @click="openIdCodeDialog(data)"
              />
              <Button
                type="button"
                outlined
                size="small"
                :label="$t('messages.invitations.admin.action.resend')"
                @click="resendInvitation(data)"
              />
              <Button
                type="button"
                outlined
                severity="danger"
                size="small"
                :label="$t('messages.invitations.admin.action.revoke')"
                @click="revokeInvitation(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>

      <Dialog
        v-model:visible="idCodeDialog.visible"
        modal
        :header="$t('messages.invitations.admin.id_code_dialog_header')"
        class="w-full max-w-md"
      >
        <p class="text-sm text-gray-600 mb-2">
          {{ $t('messages.invitations.admin.id_code_dialog_intro', { email: idCodeDialog.invitation?.email }) }}
        </p>
        <p v-if="idCodeDialog.invitation && !idCodeDialog.invitation.has_id_code" class="text-sm text-amber-700 mb-2">
          {{ $t('messages.invitations.admin.id_code_missing_hint') }}
        </p>
        <form @submit.prevent="saveIdCode">
          <label class="text-sm text-gray-600 block mb-1">{{ $t('messages.admin.user.id_code') }}</label>
          <Fluid>
            <FormInput
              v-model="idCodeForm.id_code"
              :form="idCodeForm"
              field="id_code"
              :placeholder="$t('messages.admin.user.id_code_placeholder')"
              live-validate
              data-testid="invitation-id-code-input"
            >
              <template #hint="{ valid }">
                <span v-if="valid" class="text-green-700">
                  <i class="pi pi-check-circle text-xs mr-1" />
                  {{ $t('messages.admin.user.id_code_valid') }}
                </span>
              </template>
            </FormInput>
          </Fluid>
          <div class="flex justify-end gap-2 mt-4">
            <Button type="button" text :label="$t('messages.cancel')" @click="idCodeDialog.visible = false" />
            <Button
              type="submit"
              :label="$t('messages.save')"
              :loading="idCodeForm.processing"
              :disabled="!idCodeForm.id_code || idCodeForm.invalid('id_code')"
              data-testid="invitation-id-code-save"
            />
          </div>
        </form>
      </Dialog>
    </template>
  </Card>

  <div class="flex items-center gap-4 my-6">
    <div class="flex-1 h-px bg-linear-to-r from-transparent via-gray-300 to-gray-300"></div>
    <span class="text-xs font-semibold tracking-[0.2em] text-gray-500 uppercase">{{ $t('messages.or') }}</span>
    <div class="flex-1 h-px bg-linear-to-l from-transparent via-gray-300 to-gray-300"></div>
  </div>

  <div class="mb-4 flex items-center justify-between gap-4">
    <h2 class="text-xl font-medium text-gray-800 m-0 leading-none">
      {{ $t('messages.admin.user.manage_existing') }}
    </h2>
    <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
      {{ $t('messages.admin.user.show_archived') }}
      <ToggleSwitch v-model="showArchivedRef" />
    </label>
  </div>

  <div>
    <DataTable :value="tableValue" removableSort hover stripedRows :rowClass="rowClass">
      <Column sortable field="id" header="ID">
        <template #body="slotProps">
          <Skeleton v-if="firstLoad" width="1.5rem" height="0.9rem" />
          <span v-else>{{ slotProps.data.id }}</span>
        </template>
      </Column>
      <Column sortable field="full_name" header="Nimi" class="w-48">
        <template #body="slotProps">
          <div v-if="firstLoad" class="flex flex-col gap-1">
            <Skeleton width="10rem" height="0.9rem" />
            <Skeleton width="12rem" height="0.7rem" />
          </div>
          <div v-else class="flex flex-col">
            <div class="flex items-center gap-2">
              <span>{{ slotProps.data.full_name }}</span>
              <Tag v-if="slotProps.data.blocked" :value="$t('messages.admin.user.blocked_tag')" severity="warn" class="text-xs" />
              <Tag v-if="slotProps.data.deleted_at" :value="$t('messages.admin.user.archived_tag')" severity="secondary" class="text-xs" />
            </div>
            <div class="text-sm text-gray-500">
              {{ slotProps.data.email }}
            </div>
          </div>
        </template>
      </Column>
      <Column sortable field="company.name" header="Ettevõte">
        <template #body="slotProps">
          <div v-if="firstLoad" class="flex flex-col gap-1">
            <Skeleton width="7rem" height="0.9rem" />
            <Skeleton width="5rem" height="0.7rem" />
          </div>
          <div v-else class="flex flex-col">
            <div>{{ slotProps.data.company.name }}</div>
            <div class="text-sm text-gray-500">
              {{ slotProps.data.profession_level }}
            </div>
          </div>
        </template>
      </Column>
      <Column header="EKHÜ/EKMK">
        <template #body="slotProps">
          <div v-if="firstLoad" class="flex flex-col gap-1">
            <Skeleton width="3rem" height="0.7rem" />
            <Skeleton width="3rem" height="0.7rem" />
          </div>
          <div v-else class="flex flex-col">
            <div class="text-sm" :class="slotProps.data.is_member_ekhy ? '' : 'text-gray-500'">{{ slotProps.data.is_member_ekhy ? 'EKHÜ' : '-' }}</div>
            <div class="text-sm" :class="slotProps.data.is_member_ekmk ? '' : 'text-gray-500'">{{ slotProps.data.is_member_ekmk ? 'EKMK' : '-' }}</div>
          </div>
        </template>
      </Column>
      <Column field="permissions" header="Rollid ja õigused" style="max-width: 500px;">
        <template #body="slotProps">
          <div v-if="firstLoad" class="flex flex-col gap-2">
            <Skeleton width="8rem" height="0.9rem" />
            <Skeleton width="14rem" height="0.7rem" />
            <Skeleton width="16rem" height="2rem" />
          </div>
          <template v-else>
          <div class="mb-2">
            <div v-for="role in slotProps.data.roles_display" :key="role">
              {{ role }}
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <!-- Role permissions (read-only) -->
            <div v-if="slotProps.data.role_permissions?.length > 0">
              <div class="text-xs text-gray-500 mb-1">{{ $t('messages.admin.role_permissions') }}</div>
              <div class="flex flex-wrap gap-1">
                <Tag
                  v-for="permission in slotProps.data.role_permissions"
                  :key="permission.value"
                  :value="permission.label"
                  severity="secondary"
                  class="text-xs"
                />
              </div>
            </div>
            <!-- Additional permissions (editable) -->
            <div>
              <div class="text-xs text-gray-500 mb-1">{{ $t('messages.admin.additional_permissions') }}</div>
              <!-- Show message when all permissions come from role -->
              <div
                v-if="getAvailablePermissions(slotProps.data).length === 0"
                class="text-sm text-gray-400 italic"
              >
                {{ $t('messages.admin.all_permissions_from_role') }}
              </div>
              <!-- Show MultiSelect only when there are available permissions -->
              <MultiSelect
                v-else
                :options="getAvailablePermissions(slotProps.data)"
                v-model="slotProps.data.permissions"
                optionLabel="label"
                :placeholder="$t(`messages.admin.select.permissions`)"
                display="chip"
                :showToggleAll="false"
                class="text-xs w-full"
                :pt="{ label: { class: 'flex-wrap! whitespace-normal! gap-1' } }"
                @change="permissionChange(slotProps.data.id, slotProps.data.permissions)"
              >
                <template #chip="{ value, removeCallback }">
                  <Chip :label="value.label" removable @remove="removeCallback" />
                </template>
                <template #header>
                  <div
                    class="flex items-center gap-2 py-2 px-3 mx-1 -mb-1 mt-1 leading-none hover:bg-surface-100 rounded-sm cursor-pointer"
                    @click="toggleAllPermissions(slotProps.data)"
                  >
                    <Checkbox
                      :modelValue="getRelevantPermissions(slotProps.data).length === getAvailablePermissions(slotProps.data).length"
                      :binary="true"
                      @click.stop
                      @change="toggleAllPermissions(slotProps.data)"
                    />
                    <span v-if="getRelevantPermissions(slotProps.data).length === 0">
                      {{ $t('messages.transactions.choose_all') }}
                    </span>
                    <span v-else-if="getRelevantPermissions(slotProps.data).length === getAvailablePermissions(slotProps.data).length">
                      {{ $t('messages.transactions.all') }} {{ getAvailablePermissions(slotProps.data).length }}
                      {{ $t('messages.transactions.chosen') }}
                    </span>
                    <span v-else>
                      {{ getRelevantPermissions(slotProps.data).length }} / {{ getAvailablePermissions(slotProps.data).length }}
                      {{ $t('messages.transactions.chosen') }}
                    </span>
                  </div>
                </template>
              </MultiSelect>
            </div>
          </div>
          </template>
        </template>
      </Column>
      <Column sortable field="email_verified_at" header="Kinnitatud">
        <template #body="slotProps">
          <Skeleton v-if="firstLoad" width="5rem" height="0.9rem" />
          <span v-else>{{ slotProps.data.email_verified_at }}</span>
        </template>
      </Column>
      <Column field="actions" header="Tegevused">
        <template #body="slotProps">
          <div v-if="firstLoad" class="flex gap-2">
            <Skeleton width="4rem" height="2rem" />
            <Skeleton width="5rem" height="2rem" />
          </div>
          <div v-else class="flex gap-2">
            <template v-if="slotProps.data.deleted_at">
              <Button type="button" outlined @click="restoreUser(slotProps.data)">
                {{ $t('messages.admin.user.restore') }}
              </Button>
            </template>
            <template v-else>
              <Link :href="route('admin.user.edit', slotProps.data.id)">
                <Button type="button" outlined>
                  {{ $t('messages.edit') }}
                </Button>
              </Link>
              <Button
                type="button"
                outlined
                severity="danger"
                :disabled="isSelfRow(slotProps.data)"
                v-tooltip.left="isSelfRow(slotProps.data) ? $t('messages.admin.user.cannot_archive_self_hint') : undefined"
                @click="deleteUser(slotProps.data)"
              >
                {{ $t('messages.admin.user.archive') }}
              </Button>
            </template>
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>
